








import { defineComponent, onMounted, reactive } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
const { useActions } = createNamespacedHelpers('navigationStore');
import { ResponsiveUtils } from '@olbbank/vue-components';

/**
 * Reusable quick navigation component.
 */
export default defineComponent({
	setup(_, { root }) {
		const responsiveUtils = new ResponsiveUtils();
		const { updateMenuState } = useActions(['updateMenuState', 'setActive2ndLayer']);
		const state = reactive({
			activeQuickFlyout: undefined as undefined | string,
			quickNavIds: [] as string[],
			closeMobileNav: false,
		});

		/**
		 * Set the currently active quick nav element in order to display the correct overlay.
		 */
		function setActiveQuickFlyout(id: undefined | string) {
			// in old versions of the trigger, the id is fetched from the
			// and can therefore sometimes contain a #. We have to remove that first
			if (id?.includes('#')) {
				id = id.split('#')[1];
			}
			// if no id is given and it's not undefined on purpose
			// --> set first quick nav item as layer
			if (id === '') {
				state.activeQuickFlyout = state.quickNavIds[0];
			} else {
				// when the quicknav wasn't opened by the default opener
				// but another trigger on the page:
				// close the mobile menu when the user wants to go back
				if (state.closeMobileNav && id === undefined) {
					state.closeMobileNav = false;
					// only 'close' the nav for small VP!
					if (responsiveUtils.isLgDown()) {
						updateMenuState(false);
					}
				}
				state.activeQuickFlyout = id;
			}
		}

		/**
		 * Create a list of ids of the quick nav.
		 * Reason: Sometimes, the quicknav can be opened via normal buttons but not always there's a concrete ID given.
		 * In that case, the first quick nav overlay should be rendered by default.
		 * Need to access items via normal query selector instead of slot in setup context
		 * --> Due to scoped slot, calling slot.default() doesn't work! (see https://github.com/vuejs/composition-api/issues/84)
		 */
		function getQuickNavIds() {
			const quickNavItems = document.querySelectorAll('.olb-quick-nav-item');
			for (let i = 0; i < quickNavItems.length; i++) {
				const item = quickNavItems[i] as HTMLElement;
				item.dataset.id && state.quickNavIds.push(item.dataset.id);
			}
		}

		/**
		 * Due to legacy reasons, we have to filter the page for any old quick nav triggers.
		 * That are elements, which have the class `js-quicknav-trigger`.
		 * We then need to exchange the class for an onClick listener for the new method to open a quicknav overlay.
		 */
		function transformOldTriggers() {
			const quickNavTrigger = document.querySelectorAll('.js-quicknav-trigger');
			if (quickNavTrigger.length) {
				for (let i = 0; i < quickNavTrigger.length; i++) {
					const trigger = quickNavTrigger[i] as HTMLElement;
					trigger.addEventListener('click', function(event) {
						// only trigger quick flyout display if it's not already open
						if (state.activeQuickFlyout === undefined) {
							event.preventDefault();
							event.stopImmediatePropagation();
							updateMenuState(true);
							let href = trigger.getAttribute('href');
							if (href !== null) {
								href = href.includes('#') ? href.split('#')[1] : href;
							}
							href && setActiveQuickFlyout(href);
							state.closeMobileNav = true;
						}
					});
					trigger.classList.remove('js-quicknav-trigger');
				}
			}
		}

		/**
		 * Open a specific quick nav and also handle navigation (e.g. used to open quicknav from within the stage or similar components).
		 */
		function openQuickNav(quicknav: string) {
			// if the handed quick nav id is not valid: choose the first quick nav
			if (state.quickNavIds.indexOf(quicknav) === -1) {
				quicknav = state.quickNavIds[0];
			}
			updateMenuState(true);
			setActiveQuickFlyout(quicknav);
			state.closeMobileNav = true;
		}

		onMounted(() => {
			getQuickNavIds();
			transformOldTriggers();
		});

		return { state, setActiveQuickFlyout, openQuickNav };
	},
});
